import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Switch } from '@headlessui/react'
import clsx from 'clsx'
import { Switch as Toggle } from '@mui/material'
import { Link, useLocation, NavLink } from 'react-router-dom'
import { graphql } from 'relay-runtime'
import { useFragment } from 'react-relay'
import wordmark from '../../assets/Purposely_ai_Wordmark_big_black.png'
import {
  Sidebar_User$data,
  Sidebar_User$key,
} from './__generated__/Sidebar_User.graphql'
import {
  initializeState,
  useGlobalState,
} from '../../state/v2/store'
import { AccountLockup } from './AccountLockup/AccountLockup'
import { ROUTES } from '../../routes'
import { useFeatureFlag } from '../../hooks'
import { StringParam, useQueryParam } from 'use-query-params'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { SearchableSelect } from '../../components/SearchableSelect/SearchableSelect'
import { featureFlagsMap } from '../../App'
import { GroupingMultiselectFilter } from '../../features/insights_hub/components/FilterBar/GroupingMultiselectFilter/GroupingMultiselectFilter'
import { SidebarItem } from './components/SidebarItem'
import { ReactComponent as ConceptsIcon } from '../../assets/concepts.svg';
import { ReactComponent as InsightsIcon } from '../../assets/insights.svg';
import { ReactComponent as AdvancedInsightsIcon } from '../../assets/advanced-insights.svg';
import { ReactComponent as WorkflowIcon } from '../../assets/workflow.svg';
import { ReactComponent as AdsSummaryIcon } from '../../assets/ads-summary.svg';
import { ReactComponent as LogoImage } from '../../assets/images/logo.svg';
import logo from '../../assets/images/logo.jpeg';
import { ShoppingBag, PanelRightClose, PanelRightOpen, Shield } from 'lucide-react';

const Sidebar_User = graphql`
  fragment Sidebar_User on User {
    id
    firstName
    lastName
    email
    picture
    superuser
    featureFlags

    accessibleBrands {
      id
      name
      availableKpis
      currencyType
      config {
        dataGranularity
      }
      brandFeatureFlags {
        featureFlagId
      }
    }
    account {
      id
      name
    }
  }
`

type BrandOption = Sidebar_User$data['accessibleBrands'][number]

interface SidebarProps {
  fragmentKey: Sidebar_User$key
}
export const Sidebar = ({ fragmentKey }: SidebarProps) => {
  const data = useFragment(Sidebar_User, fragmentKey)
  const [expanded, setExpanded] = useState(true)
  const [brandIds, setBrandIds] = useQueryParam('brandId', StringParam)
  const location = useLocation()
  const brandIdsArray = brandIds?.split(",") || []
  // Brand-scoped flags follow the brand(s) currently being viewed, for
  // superusers and regular users alike.
  const brandFeatureNames: string[] = Array.from(
    new Set(
      data.accessibleBrands
        .filter(brand => brandIdsArray.includes(brand.id))
        .flatMap(brand =>
          brand.brandFeatureFlags?.map(brandFeatureFlag =>
            brandFeatureFlag?.featureFlagId ? featureFlagsMap[brandFeatureFlag.featureFlagId] : undefined
          )
        )
        .filter(name => name !== undefined)
    )
  ) as string[];
  const cihEnabled = useFeatureFlag('CIH', (data.featureFlags || []).concat(brandFeatureNames))
  const workflowEnabled = useFeatureFlag('WORKFLOW', (data.featureFlags || []).concat(brandFeatureNames))
  const advancedInsightsEnabled = useFeatureFlag(
    'ADVANCED_INSIGHTS',
    (data.featureFlags || []).concat(brandFeatureNames)
  )
  const conceptingEnabled = useFeatureFlag(
    'CONCEPTING',
    (data.featureFlags || []).concat(brandFeatureNames)
  )

  const brandOptions = useMemo(() => {
    const lookup: Record<string, BrandOption> = {}
    data.accessibleBrands.forEach((brand) => (lookup[brand.id] = brand))

    return Object.values(lookup)
  }, [data])

  const { brand, selectBrand, initialized } = useGlobalState(
    (state) => ({
      brand: state.brand,
      selectBrand: state.selectBrand,
      initialized: state.initialized,
    })
  )

  const handleSelectBrand = useCallback(
    (brandOption: BrandOption[]) => {
      setBrandIds(brandOption.map(b => b.id).join(","))
      selectBrand(brandOption)
    },
    [selectBrand, setBrandIds]
  )

  const closeNav = useCallback(() => {
    setExpanded(false)
  }, [])

  // Initialize the selected brand id
  useEffect(() => {
    if (initialized) return
    if (!brandOptions.length) return
    const brandOption = initializeState(brandOptions, brandIdsArray)
    let allMatch = true;
    for(let index = 0; index < brandOption.length; index++) {
      if(!brandIdsArray.includes(brandOption[index].id)) {
        allMatch = false;
        break;
      }
    }
    if (!allMatch) {
      setBrandIds(brandOption.map(b => b.id).join(","))
    }
  }, [brandIdsArray, brandOptions, initialized, setBrandIds])

  const SidebarItems = [
    {
      label: 'Workflow',
      link: `${ROUTES.workflow.path}${location.search}`,
      visible: workflowEnabled && brandIdsArray.length == 1,
      icon: <WorkflowIcon />,
    },
    {
      label: 'Ads Summary',
      link: `${ROUTES.adsSummary.path}${location.search}`,
      visible: cihEnabled,
      icon: <AdsSummaryIcon />,
    },
    {
      label: 'Insights',
      link: `${ROUTES.insights.path}${location.search}`,
      visible: cihEnabled,
      icon: <InsightsIcon />,
    },
    {
      label: 'Advanced Insights',
      link: `${ROUTES.advancedInsights.path}${location.search}`,
      visible: advancedInsightsEnabled,
      icon: <AdvancedInsightsIcon />,
    },
    {
      label: 'Concepting',
      link: `https://concepts.app.purposely.ai/?brandId=${brandIdsArray.length > 0 ? brandIdsArray[0] : ''}`,
      external: true,
      visible: conceptingEnabled,
      icon: <ConceptsIcon />,
    },
  ];

  const isAdminRoute = location.pathname === ROUTES.admin.path
  const adminItem = {
    label: 'Admin',
    link: ROUTES.admin.path,
    icon: <Shield size={20} />,
  }
  
  return (
    <div
      className={`${!expanded ? 'w-16' : 'w-64'} bg-darkBlue text-white flex flex-col p-3 justify-between transition-all duration-300 relative h-full`}
    >
      <div className="flex flex-col space-y-4 w-full">
        <div className={`text-lg flex justify-between items-center ${!expanded ? 'flex-col' : ''}`}>
          {expanded ? (
            <LogoImage className="w-40 h-20" />
          ) : (
            <img src={logo} alt="Logo" className="w-9 rounded-lg" />
          )}

          <button
            onClick={() => setExpanded(!expanded)}
            className={`rounded-full hover:bg-primary-light transition-all duration-300 ease-in-out transform ${
              !expanded ? 'mt-4' : ''
            }`}
          >
            {expanded ? (
              <PanelRightOpen size={20} className="text-white" />
            ) : (
              <PanelRightClose size={20} />
            )}
          </button>
        </div>

        {(data.superuser ?? false) && (
          <div>
            <ul className="space-y-1">
              <li className="py-1">
                <SidebarItem item={adminItem} expanded={expanded} />
              </li>
            </ul>
            <hr className="my-2 border-white/20" />
          </div>
        )}

        {/* <img src={wordmark} alt={'wordmark'} /> */}
        {!isAdminRoute && brand && (
          <GroupingMultiselectFilter
            options={Object.entries(
              brandOptions.reduce((acc, brand) => {
                const group = brand.currencyType || "USD";
                if (!acc[group]) {
                  acc[group] = [];
                }
                acc[group].push(brand);
                return acc;
              }, {} as Record<string, typeof brandOptions>)
            ).map(([group, options]) => ({
              group,
              options: options.sort((a, b) => a.name.localeCompare(b.name)),
            })).sort((a, b) => a.group.localeCompare(b.group))}
            getOptionLabel={(o) => o.name}
            getOptionKey={(o) => o.id}
            onChange={handleSelectBrand}
            defaultValue={brand.map((b) => {
              const brandOption = brandOptions.find(option => option.id === b.id);
              return brandOption || { ...b, brandFeatureFlags: [], currencyType: null };
            })}
            label="Brand"
            placeholder="Choose brand(s) within same currency"
            expanded={expanded}
          />
        )}
        <ul className="space-y-1">
        {SidebarItems.filter(item => item.visible).map((item, index) => (
          <li key={index} className="py-1">
            <SidebarItem item={item} expanded={expanded} />
          </li>
        ))}
        </ul>
      </div>

      <div className="mt-auto flex w-full justify-start">
        <AccountLockup user={data} expanded={expanded} />
      </div>
    </div>
  )
}
